import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, CreditCard, Banknote, FileText, MapPin, Phone, Mail, Clock, MessageSquare, CheckCircle2, CalendarDays, Loader2, Info, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { sendFormEmail } from '../utils/sendFormEmail';
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('bark') || n.includes('compost');
}
const CENTER: [number, number] = [47.3087, -122.0032]; // Black Diamond, WA
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function getZoneInfo(miles: number) {
  if (miles <= 7) return {
    zone: 1,
    fee: 40
  };
  if (miles <= 13) return {
    zone: 2,
    fee: 75
  };
  if (miles <= 18) return {
    zone: 3,
    fee: 120
  };
  if (miles <= 22) return {
    zone: 4,
    fee: 150
  };
  return {
    zone: null,
    fee: 0
  }; // Custom pricing needed
}
type DrawerView = 'cart' | 'checkout' | 'success';
export function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo
  } = useCart();
  const navigate = useNavigate();
  const [view, setView] = useState<DrawerView>('cart');
  // Checkout form state
  const [addressInput, setAddressInput] = useState(deliveryAddress);
  const [addressError, setAddressError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [instructions, setInstructions] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'delivery'>('delivery');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const totalDeliveryFee = deliveryFee * deliveryCount;
  const finalTotal = totalPrice + totalDeliveryFee;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  // Refs for auto-advance
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim() || view !== 'cart') {
      if (!addressInput.trim()) {
        clearDeliveryInfo();
        setAddressError('');
      }
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address.');
          clearDeliveryInfo();
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
          const zoneInfo = getZoneInfo(distance);
          setDeliveryInfo(addressInput, zoneInfo.fee, zoneInfo.zone);
          // Auto-advance to name if address is valid
          if (!name) {
            setTimeout(() => nameRef.current?.focus(), 100);
          }
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        clearDeliveryInfo();
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput, view, setDeliveryInfo, clearDeliveryInfo, setIsCalculatingDelivery]);
  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return val;
    if (!match[2]) return match[1];
    if (!match[3]) return `(${match[1]}) ${match[2]}`;
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  };
  const isAddressValid = addressInput && !addressError && !isCalculatingDelivery;
  const isNameValid = name.trim().length > 1;
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isDateValid = !!deliveryDate;
  const handleContinueShopping = () => {
    closeCart();
    setView('cart');
    const el = document.getElementById('materials');
    if (el) {
      setTimeout(() => el.scrollIntoView({
        behavior: 'smooth'
      }), 300);
    }
  };
  const handleCheckout = () => {
    // Validate required fields before proceeding
    if (!addressInput.trim() || !name.trim() || !phone.trim() || !email.trim() || !deliveryDate || !deliveryTime) {
      // We could set an error state here, but for now we'll just rely on HTML5 form validation
      // by triggering a form submit on a hidden button, or just alert.
      // Since we are moving this to a form, we should let the form handle the submit.
      return;
    }
    setView('checkout');
  };
  const handleCartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setView('checkout');
  };
  const handleBackToCart = () => {
    setView('cart');
  };
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessingPayment(true);
    try {
      const orderItems = items.map(item => {
        const isBark = isLightMaterial(item.product);
        const minChargeYards = 5;
        const smallLoadThreshold = isBark ? 12 : 10;
        const billableYards = Math.max(item.quantity, minChargeYards);
        const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
        const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
        let total = billableYards * effectivePrice;
        const bulkPct = item.quantity >= 48 ? 0.1 : item.quantity >= 24 ? 0.05 : 0;
        const hasBulk = bulkPct > 0;
        if (hasBulk) {
          total = Math.round(total * (1 - bulkPct));
        }
        const minNote = item.quantity < minChargeYards ? ` (${minChargeYards} yd min charge)` : '';
        const bulkNote = hasBulk ? ` (${Math.round(bulkPct * 100)}% bulk discount)` : '';
        return `${item.product} (${item.category}) — ${item.quantity} yds${minNote}${bulkNote} = ${total}`;
      }).join('\n');
      const timeLabel = deliveryTime === 'morning' ? 'Morning (7-11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12-4 PM)' : 'Flexible';
      const deliveryFeeLabel = totalDeliveryFee > 0 ? `${totalDeliveryFee.toFixed(2)}` : deliveryZone === null ? 'Custom Quote' : 'FREE';
      const orderSummary = `Order Summary:\n${orderItems}\n\nTotal Yards: ${totalYards}\nMaterials Total: ${totalPrice.toFixed(2)}\nDelivery Fee: ${deliveryFeeLabel}\nOrder Total: ${finalTotal.toFixed(2)}\n\nDelivery Address: ${addressInput}\nRequested Date: ${deliveryDate}\nTime Window: ${timeLabel}\nSpecial Instructions: ${instructions || 'None'}\nPayment: ${paymentMethod === 'card' ? 'Pay Online' : 'Pay on Delivery'}`;
      await sendFormEmail({
        subject: '253Gravel — New Order Submission',
        to_name: name,
        from_name: '253 Gravel',
        'Order Items': orderItems,
        'Total Yards': String(totalYards),
        'Materials Total': `${totalPrice.toFixed(2)}`,
        'Delivery Fee': deliveryFeeLabel,
        'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone}` : 'Outside standard zones',
        'Order Total': `${finalTotal.toFixed(2)}`,
        Name: name,
        'Delivery Address': addressInput,
        Phone: phone,
        email: email,
        _replyto: email,
        _autoresponse: `Hi ${name}! Thank you for your order request with 253 Gravel! We've received your details and will contact you shortly to verify your order and confirm delivery.\n\n${orderSummary}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The 253 Gravel Team`,
        message: `Hi ${name}! Thank you for your order request with 253 Gravel! We've received your details and will contact you shortly to verify your order and confirm delivery.\n\n${orderSummary}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The 253 Gravel Team`,
        'Delivery Date': deliveryDate,
        'Delivery Time': timeLabel,
        'Special Instructions': instructions || 'None',
        'Payment Method': paymentMethod === 'card' ? 'Pay Online' : 'Pay on Delivery'
      });
    } catch (err) {
      console.error('Order submission error:', err);
    } finally {
      setIsProcessingPayment(false);
    }
    setView('success');
  };
  const handleCloseSuccess = () => {
    setView('cart');
    setAddressInput('');
    setName('');
    setPhone('');
    setEmail('');
    setDeliveryDate('');
    setDeliveryTime('');
    setInstructions('');
    clearCart();
    closeCart();
  };
  const inputClass = 'w-full px-4 py-3.5 bg-white/95 dark:bg-[#2a2a2a] backdrop-blur-sm border border-stone-200 dark:border-white/10 rounded-xl text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:shadow-[0_0_15px_rgba(255,140,50,0.15)] transition-all text-base font-medium';
  // Helper to build delivery zones URL with address
  const deliveryZonesUrl = (addr?: string) => {
    const params = addr?.trim() ? `?address=${encodeURIComponent(addr)}` : '';
    return `/#/delivery-zones${params}`;
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  return <AnimatePresence data-id="element-119">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => {
        closeCart();
        setView('cart');
      }} className="fixed inset-0 bg-black/70 backdrop-blur-md z-[60]" data-id="element-120" />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[70] pointer-events-none" data-id="element-121">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="w-full max-w-5xl bg-white/95 dark:bg-[#2a2a2a]/90 backdrop-blur-xl shadow-[0_8px_60px_rgba(0,0,0,0.5)] flex flex-col rounded-2xl border border-white/20 dark:border-white/10 max-h-[90vh] overflow-hidden pointer-events-auto" data-id="element-122">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-white/20 dark:border-white/10 shrink-0 bg-gradient-to-b from-white/50 to-transparent dark:from-white/5 dark:to-transparent" data-id="element-123">
                <div className="flex items-center space-x-4" data-id="element-124">
                  {view === 'checkout' && <button onClick={handleBackToCart} className="p-2 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-lg hover:bg-black/5 dark:hover:bg-white/10 mr-1" data-id="element-125">
                      <ArrowLeft className="w-6 h-6" data-id="element-126" />
                    </button>}
                  <ShoppingCart className="w-7 h-7 text-brand-charcoal dark:text-white" data-id="element-127" />
                  <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-128">
                    {view === 'cart' && 'Your Cart'}
                    {view === 'checkout' && 'Checkout'}
                    {view === 'success' && 'Order Submitted'}
                  </h2>
                  {view === 'cart' && <span className="bg-black/5 dark:bg-white/10 text-brand-charcoal dark:text-stone-200 text-sm font-bold px-3 py-1.5 rounded-lg border border-white/20 dark:border-white/5" data-id="element-129">
                      {items.length} {items.length === 1 ? 'item' : 'items'}
                    </span>}
                </div>
                <button onClick={() => {
              closeCart();
              setView('cart');
            }} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10" data-id="element-130">
                  <X className="w-6 h-6" data-id="element-131" />
                </button>
              </div>
              {/* ===== CART VIEW ===== */}
              {view === 'cart' && <form id="cart-form" onSubmit={handleCartSubmit} className="flex-1 overflow-y-auto" data-id="element-132">
                  {/* Items */}
                  <div className="p-8" data-id="element-133">
                    {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-16" data-id="element-134">
                        <div className="w-28 h-28 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center border border-white/20 dark:border-white/10 shadow-inner" data-id="element-135">
                          <ShoppingCart className="w-12 h-12 text-brand-muted opacity-50" data-id="element-136" />
                        </div>
                        <div data-id="element-137">
                          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-138">
                            Your cart is empty
                          </h3>
                          <p className="text-brand-muted dark:text-stone-300 text-lg max-w-[300px] mx-auto" data-id="element-139">
                            Looks like you haven't added any materials to your
                            cart yet.
                          </p>
                        </div>
                        <button onClick={handleContinueShopping} className="bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white px-10 py-4 rounded-xl font-bold text-lg transition-colors border border-transparent dark:border-white/10 backdrop-blur-sm" data-id="element-140">
                          Browse Materials
                        </button>
                      </div> : <div className="space-y-5" data-id="element-141">
                        {items.map(item => <div key={item.id} className="flex flex-col sm:flex-row gap-5 p-5 bg-white/50 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-white/30 dark:border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-142">
                            <div className="flex-1" data-id="element-143">
                              <p className="text-sm font-bold text-brand-muted dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-144">
                                {item.category}
                              </p>
                              <h4 className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white mb-1.5" data-id="element-145">
                                {item.product}
                              </h4>
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const minChargeYards = 5;
                      const smallLoadThreshold = isLight ? 12 : 10;
                      const billableYards = Math.max(item.quantity, minChargeYards);
                      const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      const bulkPct = item.quantity >= 48 ? 0.1 : item.quantity >= 24 ? 0.05 : 0;
                      const hasBulk = bulkPct > 0;
                      const displayPrice = hasBulk ? Math.ceil(effectivePrice * (1 - bulkPct)) : effectivePrice;
                      return <p className="text-base text-brand-muted dark:text-stone-200 font-medium" data-id="element-146">
                                    ${displayPrice} / yard
                                    {hasBulk && <span className="text-sm text-green-600 dark:text-green-400 ml-1.5 font-semibold" data-id="element-147">
                                        ({Math.round(bulkPct * 100)}% bulk
                                        discount)
                                      </span>}
                                    {!hasBulk && item.quantity < minChargeYards && <span className="text-sm text-brand-orange ml-1.5" data-id="element-148">
                                          ({minChargeYards} yd min charge)
                                        </span>}
                                    {!hasBulk && item.quantity >= minChargeYards && billableYards < smallLoadThreshold && <span className="text-sm text-brand-orange ml-1.5" data-id="element-149">
                                          (small load · ${item.pricePerYard}/yd
                                          at {smallLoadThreshold}+)
                                        </span>}
                                  </p>;
                    })()}
                            </div>
                            <div className="flex items-center justify-between gap-4" data-id="element-150">
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const minChargeYards = 5;
                      const smallLoadThreshold = isLight ? 12 : 10;
                      const billableYards = Math.max(item.quantity, minChargeYards);
                      const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      let itemTotal = billableYards * effectivePrice;
                      // Aggregate yards by product for bulk discount (strip delivery suffix)
                      const baseProduct = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
                      const aggYards = items.filter(i => baseProduct(i.product) === baseProduct(item.product)).reduce((s, i) => s + i.quantity, 0);
                      const bulkPct2 = aggYards >= 48 ? 0.1 : aggYards >= 24 ? 0.05 : 0;
                      const hasBulk = bulkPct2 > 0;
                      if (hasBulk) {
                        itemTotal = Math.round(itemTotal * (1 - bulkPct2));
                      }
                      return <div data-id="element-151">
                                    <p className="font-heading font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-152">
                                      ${itemTotal}
                                    </p>
                                    {hasBulk && <p className="text-xs text-green-600 dark:text-green-400 font-semibold" data-id="element-153">
                                        {Math.round(bulkPct2 * 100)}% bulk
                                        discount
                                      </p>}
                                  </div>;
                    })()}
                              <div className="flex items-center space-x-3 self-center" data-id="element-154">
                                {(() => {
                        const isBarkMulch = isLightMaterial(item.product);
                        const fullLoad = isBarkMulch ? 15 : 12;
                        const isFullLoadMultiple = item.quantity % fullLoad === 0;
                        const loadCount = item.quantity / fullLoad;
                        const handleMinus = () => {
                          if (isFullLoadMultiple && loadCount > 1) {
                            updateQuantity(item.id, item.quantity - fullLoad);
                          } else {
                            updateQuantity(item.id, item.quantity - 1);
                          }
                        };
                        const maxQty = isBarkMulch ? 60 : 48;
                        const handlePlus = () => {
                          if (item.quantity >= maxQty) return;
                          if (isFullLoadMultiple) {
                            updateQuantity(item.id, Math.min(item.quantity + fullLoad, maxQty));
                          } else {
                            updateQuantity(item.id, Math.min(item.quantity + 1, maxQty));
                          }
                        };
                        return <div className="flex flex-col items-center gap-1" data-id="element-155">
                                      <div className="flex items-center bg-white/60 dark:bg-white/10 backdrop-blur-sm border border-white/30 dark:border-white/10 rounded-lg overflow-hidden min-w-[140px]" data-id="element-156">
                                        <button type="button" onClick={handleMinus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" data-id="element-157">
                                          <Minus className="w-5 h-5" data-id="element-158" />
                                        </button>
                                        <div className="flex items-center justify-center py-1.5 flex-1 whitespace-nowrap gap-1.5" data-id="element-159">
                                          <span className="font-bold text-base text-brand-charcoal dark:text-white leading-none" data-id="element-160">
                                            {isFullLoadMultiple ? loadCount : item.quantity}
                                          </span>
                                          <span className="text-[10px] text-brand-muted dark:text-stone-300 uppercase tracking-wider font-bold" data-id="element-161">
                                            {isFullLoadMultiple ? loadCount === 1 ? 'Full Load' : 'Full Loads' : 'yd'}
                                          </span>
                                        </div>
                                        <button type="button" onClick={handlePlus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" data-id="element-162">
                                          <Plus className="w-5 h-5" data-id="element-163" />
                                        </button>
                                      </div>
                                      <span className="text-xs text-stone-400 dark:text-stone-400 font-medium" data-id="element-164">
                                        ≈{' '}
                                        {(item.quantity * getDensity(item.product)).toFixed(1)}{' '}
                                        tons
                                      </span>
                                    </div>;
                      })()}
                                <button onClick={() => removeFromCart(item.id)} className="p-2.5 text-brand-muted hover:text-brand-orange transition-colors" title="Remove item" data-id="element-165">
                                  <Trash2 className="w-5 h-5" data-id="element-166" />
                                </button>
                              </div>
                            </div>
                          </div>)}
                      </div>}
                  </div>

                  {/* Contractor Prompt */}
                  {(() => {
              const productYards: Record<string, number> = {};
              const baseName = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
              for (const item of items) {
                const base = baseName(item.product);
                productYards[base] = (productYards[base] || 0) + item.quantity;
              }
              return Object.values(productYards).some(y => y > 24);
            })() && <div className="mx-8 mb-2 p-4 bg-brand-orange/10 dark:bg-brand-orange/10 border border-brand-orange/30 rounded-xl flex items-start gap-3" data-id="element-167">
                      <Info className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-168" />
                      <div data-id="element-169">
                        <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-170">
                          Are you a contractor?
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-171">
                          Get priority scheduling, volume pricing, and dedicated
                          support.{' '}
                          <a href="/#/subcontractors" className="text-brand-orange font-bold underline hover:text-brand-orange/80 transition-colors" onClick={() => closeCart()} data-id="element-172">
                            Sign up on our Contractors page →
                          </a>
                        </p>
                      </div>
                    </div>}

                  {/* Delivery Details */}
                  {items.length > 0 && <div className="px-8 pb-8 pt-8" data-id="element-173">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-stone-500 tracking-tight mb-5" data-id="element-174">
                        Delivery Details
                      </h3>
                      <div className="space-y-4 bg-stone-50 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-stone-200 dark:border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] p-5" data-id="element-175">
                        {/* Address */}
                        <div data-id="element-176">
                          <label className="flex items-center justify-between text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-177">
                            <span className="flex items-center" data-id="element-178">
                              <MapPin className="w-5 h-5 mr-2 text-brand-muted" data-id="element-179" />
                              Delivery Address
                            </span>
                            {isCalculatingDelivery && <span className="text-sm text-brand-orange flex items-center" data-id="element-180">
                                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" data-id="element-181" />
                                Calculating...
                              </span>}
                          </label>
                          <input type="text" value={addressInput} onChange={e => setAddressInput(e.target.value)} placeholder="123 Main St, Tacoma, WA" autoComplete="street-address" className={inputClass} required data-id="element-182" />
                          {addressError && <p className="text-sm text-red-500 mt-1.5 font-medium" data-id="element-183">
                              {addressError}
                            </p>}
                          {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-sm text-green-600 dark:text-green-400 mt-1.5 flex items-center font-medium" data-id="element-184">
                                <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-185" />
                                Zone {deliveryZone} (
                                {deliveryFee === 0 ? 'Free Delivery' : deliveryCount > 1 ? `+$${totalDeliveryFee.toFixed(0)} for ${deliveryCount} deliveries` : `+$${deliveryFee} Delivery`}
                                ) —{' '}
                                <a href={deliveryZonesUrl(addressInput)} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300 transition-colors" data-id="element-186">
                                  View Zones
                                </a>
                              </p>}
                          {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-sm text-brand-orange mt-1.5 flex items-center font-medium" data-id="element-187">
                                <Info className="w-4 h-4 mr-1" data-id="element-188" />
                                Outside standard zones. We'll call with a custom
                                quote.
                              </p>}
                        </div>

                        {/* Name */}
                        <div data-id="element-189">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-190">
                            <FileText className="w-5 h-5 mr-2 text-brand-muted" data-id="element-191" />
                            Full Name
                          </label>
                          <input ref={nameRef} type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && isNameValid) {
                      e.preventDefault();
                      setTimeout(() => phoneRef.current?.focus(), 100);
                    }
                  }} placeholder="John Doe" autoComplete="name" className={`${inputClass} ${isAddressValid && !isNameValid ? 'ring-2 ring-[#2e8a44] border-[#2e8a44]' : ''}`} required data-id="element-192" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-193">
                          <div data-id="element-194">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-195">
                              <Phone className="w-5 h-5 mr-2 text-brand-muted" data-id="element-196" />
                              Phone Number
                            </label>
                            <input ref={phoneRef} type="tel" value={phone} onChange={e => {
                      const formatted = formatPhone(e.target.value);
                      setPhone(formatted);
                      if (formatted.length === 14) {
                        setTimeout(() => emailRef.current?.focus(), 100);
                      }
                    }} placeholder="(206) 555-1234" autoComplete="tel" className={`${inputClass} ${isAddressValid && isNameValid && !isPhoneValid ? 'ring-2 ring-[#2e8a44] border-[#2e8a44]' : ''}`} required data-id="element-197" />
                          </div>
                          <div data-id="element-198">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-199">
                              <Mail className="w-5 h-5 mr-2 text-brand-muted" data-id="element-200" />
                              Email Address
                            </label>
                            <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && isEmailValid) {
                        e.preventDefault();
                        setShowCalendar(true);
                        setTimeout(() => dateBtnRef.current?.focus(), 100);
                      }
                    }} placeholder="you@example.com" autoComplete="email" className={`${inputClass} ${isAddressValid && isNameValid && isPhoneValid && !isEmailValid ? 'ring-2 ring-[#2e8a44] border-[#2e8a44]' : ''}`} required data-id="element-201" />
                          </div>
                        </div>

                        {/* Date & Time Row */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-202">
                          <div className={`relative ${showCalendar ? 'z-[100]' : ''}`} data-id="element-203">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-204">
                              <CalendarDays className="w-5 h-5 mr-2 text-brand-muted" data-id="element-205" />
                              Date
                            </label>
                            <button ref={dateBtnRef} type="button" onClick={() => setShowCalendar(!showCalendar)} className={`${inputClass} text-left flex items-center justify-between w-full ${isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid ? 'ring-2 ring-[#2e8a44] border-[#2e8a44]' : ''}`} data-id="element-206">
                              <span className={deliveryDate ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-207">
                                {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Select'}
                              </span>
                              <CalendarDays className="w-5 h-5 text-stone-300" data-id="element-208" />
                            </button>
                            {/* Calendar Dropdown */}
                            <AnimatePresence data-id="element-209">
                              {showCalendar && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-[320px] bg-white/95 dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-5" data-id="element-210">
                                  {(() => {
                          const today = new Date();
                          const calMonth = calendarMonth || new Date(today.getFullYear(), today.getMonth(), 1);
                          const year = calMonth.getFullYear();
                          const month = calMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0, 0).getDate();
                          const monthName = calMonth.toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric'
                          });
                          const days = [];
                          for (let i = 0; i < firstDay; i++) days.push(null);
                          for (let d = 1; d <= daysInMonth; d++) days.push(d);
                          return <>
                                        <div className="flex items-center justify-between mb-4" data-id="element-211">
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-212">
                                            <ChevronLeft className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-213" />
                                          </button>
                                          <span className="text-base font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-214">
                                            {monthName}
                                          </span>
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-215">
                                            <ChevronRight className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-216" />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-217">
                                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <span key={d} className="text-xs font-bold text-stone-500 dark:text-stone-400 py-1 uppercase" data-id="element-218">
                                              {d}
                                            </span>)}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1" data-id="element-219">
                                          {days.map((day, i) => {
                                if (day === null) return <span key={`empty-${i}`} data-id="element-220" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const isPast = dateStr < minDate;
                                const isSelected = dateStr === deliveryDate;
                                const isToday = dateStr === today.toISOString().split('T')[0];
                                return <button key={dateStr} type="button" disabled={isPast} onClick={() => {
                                  setDeliveryDate(dateStr);
                                  setShowCalendar(false);
                                  setTimeout(() => {
                                    timeBtnRef.current?.focus();
                                    setShowTimeDropdown(true);
                                  }, 100);
                                }} className={`text-base font-bold py-2 rounded-xl transition-colors ${isPast ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : isSelected ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : isToday ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-221">
                                                {day}
                                              </button>;
                              })}
                                        </div>
                                      </>;
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <div className="relative" data-id="element-222">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-223">
                              <Clock className="w-5 h-5 mr-2 text-brand-muted" data-id="element-224" />
                              Time
                            </label>
                            <input type="hidden" value={deliveryTime} required data-id="element-225" />
                            <button ref={timeBtnRef} type="button" onClick={() => setShowTimeDropdown(!showTimeDropdown)} className={`${inputClass} text-left flex items-center justify-between w-full ${isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime ? 'ring-2 ring-[#2e8a44] border-[#2e8a44]' : ''}`} data-id="element-226">
                              <span className={deliveryTime ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-227">
                                {deliveryTime === 'morning' ? 'Morning (7–11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12–4 PM)' : deliveryTime === 'flexible' ? 'Flexible' : 'Select'}
                              </span>
                              <Clock className="w-5 h-5 text-stone-300" data-id="element-228" />
                            </button>
                            <AnimatePresence data-id="element-229">
                              {showTimeDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-full bg-white/95 dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-3 space-y-1" data-id="element-230">
                                  {[{
                          value: 'morning',
                          label: 'Morning (7–11 AM)'
                        }, {
                          value: 'afternoon',
                          label: 'Afternoon (12–4 PM)'
                        }, {
                          value: 'flexible',
                          label: 'Flexible'
                        }].map(opt => <button key={opt.value} type="button" onClick={() => {
                          setDeliveryTime(opt.value);
                          setShowTimeDropdown(false);
                        }} className={`w-full text-left px-4 py-3 rounded-xl text-base font-bold transition-colors ${deliveryTime === opt.value ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-231">
                                      {opt.label}
                                    </button>)}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Same day callout */}
                        <p className="text-sm text-stone-300 flex items-center gap-1.5 font-medium" data-id="element-232">
                          <Phone className="w-3.5 h-3.5 text-stone-400" data-id="element-233" />
                          Same day? Call{' '}
                          <a href="tel:206-619-3742" className="font-bold text-brand-orange hover:underline" data-id="element-234">
                            (206) 619-3742
                          </a>
                        </p>

                        {/* Instructions */}
                        <div data-id="element-235">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-500 mb-2" data-id="element-236">
                            <MessageSquare className="w-5 h-5 mr-2 text-brand-muted" data-id="element-237" />
                            Special Instructions
                          </label>
                          <textarea value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Dump on left side of driveway, gate code 1234..." rows={2} className={`${inputClass} resize-none`} data-id="element-238" />
                        </div>
                      </div>
                    </div>}

                  {/* Summary */}
                  {items.length > 0 && <div className="px-8 pb-8 border-t border-white/20 dark:border-white/10 pt-8 bg-gradient-to-t from-white/50 to-transparent dark:from-white/5 dark:to-transparent" data-id="element-239">
                      <div className="space-y-3 mb-6" data-id="element-240">
                        <div className="flex justify-between items-end" data-id="element-241">
                          <span className="text-brand-muted dark:text-stone-300 font-bold text-base" data-id="element-242">
                            Materials ({totalYards} yards · ≈{' '}
                            {items.reduce((sum, item) => sum + item.quantity * getDensity(item.product), 0).toFixed(1)}{' '}
                            tons
                            {deliveryCount > 1 ? ` · ${deliveryCount} loads` : ''}
                            )
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-243">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>
                        {deliveryCount > 1 && <div className="flex items-start gap-2 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-lg backdrop-blur-sm" data-id="element-244">
                            <Info className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-245" />
                            <p className="text-sm text-stone-300 font-medium" data-id="element-246">
                              Your order requires {deliveryCount} separate
                              deliveries. Each delivery has its own delivery
                              fee.
                            </p>
                          </div>}

                        {/* Delivery Fee Row */}
                        <div className="flex justify-between items-center" data-id="element-247">
                          <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-248">
                            Delivery Fee
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-249">
                            {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-250" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-251">
                                Custom Quote
                              </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-252">
                                FREE
                              </span> : deliveryFee > 0 ? <>
                                ${totalDeliveryFee.toFixed(2)}
                                {deliveryCount > 1 && <span className="text-sm text-stone-400 ml-1.5" data-id="element-253">
                                    ({deliveryCount} × ${deliveryFee.toFixed(2)}
                                    )
                                  </span>}
                              </> : <span className="text-stone-500 text-sm font-medium" data-id="element-254">
                                Enter address above
                              </span>}
                          </span>
                        </div>

                        <div className="flex justify-between items-end pt-4 border-t border-white/20 dark:border-white/10" data-id="element-255">
                          <span className="text-brand-charcoal dark:text-white font-extrabold text-xl" data-id="element-256">
                            Total Estimate
                          </span>
                          <span className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-257">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4" data-id="element-258">
                        <button type="submit" className="w-full flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white py-5 rounded-xl font-extrabold text-lg transition-all group shadow-[0_0_20px_rgba(255,140,50,0.3)] hover:shadow-[0_0_25px_rgba(255,140,50,0.5)]" data-id="element-259">
                          Proceed to Checkout
                          <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-260" />
                        </button>
                        <button type="button" onClick={handleContinueShopping} className="w-full py-5 rounded-xl font-extrabold text-lg text-brand-charcoal dark:text-white border border-white/30 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-261">
                          Add More Materials
                        </button>
                      </div>
                      <p className="text-sm text-brand-charcoal/70 dark:text-white/95 text-center mt-5 font-medium" data-id="element-262">
                        Need more than 12 yards or a custom amount?{' '}
                        <a href="tel:206-619-3742" className="text-brand-orange hover:underline font-bold" data-id="element-263">
                          Call us
                        </a>{' '}
                        or{' '}
                        <a href="/contact" className="text-brand-orange hover:underline font-bold" data-id="element-264">
                          contact us
                        </a>
                        .
                      </p>
                    </div>}
                </form>}
              {/* ===== CHECKOUT VIEW ===== */}
              {view === 'checkout' && <div className="flex-1 overflow-y-auto p-8" data-id="element-265">
                  <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-8" data-id="element-266">
                    {/* Order Summary */}
                    <div data-id="element-267">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-268">
                        Order Summary
                      </h3>
                      <div className="space-y-3 bg-white/50 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-white/30 dark:border-white/10 p-5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-269">
                        {items.map(item => <div key={item.id} className="flex justify-between items-center text-base py-3 border-b border-white/20 dark:border-white/10 last:border-0" data-id="element-270">
                            <div data-id="element-271">
                              <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-272">
                                {item.product}
                              </span>
                              <span className="text-brand-muted dark:text-stone-400 ml-2 font-medium" data-id="element-273">
                                × {item.quantity} yds · ≈{' '}
                                {(item.quantity * getDensity(item.product)).toFixed(1)}{' '}
                                tons
                              </span>
                            </div>
                            <span className="font-extrabold text-brand-charcoal dark:text-white" data-id="element-274">
                              $
                              {(() => {
                        const surcharge = item.quantity < 10 ? 1 + (10 - item.quantity) * 0.1 : 1;
                        return (item.quantity * Math.ceil(item.pricePerYard * surcharge)).toFixed(2);
                      })()}
                            </span>
                          </div>)}
                        <div className="space-y-3 pt-3" data-id="element-275">
                          <div className="flex justify-between items-center" data-id="element-276">
                            <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-277">
                              Subtotal
                            </span>
                            <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-278">
                              ${totalPrice.toFixed(2)}
                            </span>
                          </div>

                          {/* Delivery Fee Row */}
                          <div className="flex justify-between items-center" data-id="element-279">
                            <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-280">
                              Delivery Fee
                            </span>
                            <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-281">
                              {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-282" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-283">
                                  Custom Quote
                                </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-284">
                                  FREE
                                </span> : deliveryFee > 0 ? <>
                                  {deliveryCount > 1 && <span className="text-sm text-stone-400 mr-1.5" data-id="element-285">
                                      ({deliveryCount} × $
                                      {deliveryFee.toFixed(2)})
                                    </span>}
                                  ${totalDeliveryFee.toFixed(2)}
                                </> : <span className="text-stone-500 text-sm font-medium" data-id="element-286">
                                  Based on address
                                </span>}
                            </span>
                          </div>

                          <div className="flex justify-between items-center pt-4 border-t border-white/20 dark:border-white/10" data-id="element-287">
                            <span className="font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-288">
                              Total
                            </span>
                            <span className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-289">
                              ${finalTotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Details Summary (Read-Only) */}
                    <div data-id="element-290">
                      <div className="flex items-center justify-between mb-4" data-id="element-291">
                        <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-292">
                          Delivery Details
                        </h3>
                        <button type="button" onClick={handleBackToCart} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-293">
                          Edit
                        </button>
                      </div>
                      <div className="bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/30 dark:border-white/10 rounded-xl p-5 space-y-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-294">
                        <div className="flex items-start gap-3" data-id="element-295">
                          <MapPin className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-296" />
                          <div data-id="element-297">
                            <p className="text-base text-brand-charcoal dark:text-white font-bold" data-id="element-298">
                              {addressInput}
                            </p>
                            {deliveryZone !== null ? <p className="text-sm text-green-600 dark:text-green-400 mt-0.5 font-medium" data-id="element-299">
                                Zone {deliveryZone}
                              </p> : <p className="text-sm text-brand-orange mt-0.5 font-medium" data-id="element-300">
                                Outside standard zones (Custom Quote)
                              </p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-301">
                          <Phone className="w-5 h-5 text-brand-muted shrink-0" data-id="element-302" />
                          <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-303">
                            {phone}
                          </p>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-304">
                          <CalendarDays className="w-5 h-5 text-brand-muted shrink-0" data-id="element-305" />
                          <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-306">
                            {new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                            <span className="text-brand-muted dark:text-stone-400 mx-2" data-id="element-307">
                              •
                            </span>
                            <span className="capitalize" data-id="element-308">{deliveryTime}</span>
                          </p>
                        </div>
                        {instructions && <div className="flex items-start gap-3 pt-3 border-t border-white/20 dark:border-white/10" data-id="element-309">
                            <MessageSquare className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-310" />
                            <p className="text-base text-brand-charcoal dark:text-stone-300 italic font-medium" data-id="element-311">
                              "{instructions}"
                            </p>
                          </div>}
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div data-id="element-312">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-313">
                        Payment Method
                      </h3>
                      <div className="grid grid-cols-2 gap-4 mb-5" data-id="element-314">
                        <button type="button" disabled className="flex flex-col items-center p-5 rounded-xl border transition-all border-white/30 dark:border-white/10 bg-white/30 dark:bg-white/5 backdrop-blur-sm opacity-50 cursor-not-allowed" data-id="element-315">
                          <CreditCard className="w-7 h-7 mb-2 text-brand-charcoal dark:text-stone-200" data-id="element-316" />
                          <span className="text-base font-extrabold text-brand-charcoal dark:text-stone-200" data-id="element-317">
                            Pay Online
                          </span>
                          <span className="text-xs text-stone-700 dark:text-stone-200 mt-1 font-medium text-center" data-id="element-318">
                            Online payment coming soon
                          </span>
                        </button>
                        <button type="button" onClick={() => setPaymentMethod('delivery')} className={`flex flex-col items-center p-5 rounded-xl border transition-all backdrop-blur-sm ${paymentMethod === 'delivery' ? 'border-brand-orange bg-brand-orange/10 dark:bg-brand-orange/10 shadow-[inset_0_0_20px_rgba(255,140,50,0.1)]' : 'border-white/30 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10'}`} data-id="element-319">
                          <Banknote className={`w-7 h-7 mb-2 ${paymentMethod === 'delivery' ? 'text-brand-orange' : 'text-brand-charcoal dark:text-stone-200'}`} data-id="element-320" />
                          <span className={`text-base font-extrabold ${paymentMethod === 'delivery' ? 'text-brand-orange' : 'text-brand-charcoal dark:text-stone-200'}`} data-id="element-321">
                            Pay on Delivery
                          </span>
                          <span className="text-xs text-stone-700 dark:text-stone-200 mt-1 font-medium" data-id="element-322">
                            Cash, card, or check
                          </span>
                        </button>
                      </div>
                    </div>
                  </form>
                  {/* Checkout Footer */}
                  <div className="p-8 bg-gradient-to-t from-white/50 to-transparent dark:from-white/5 dark:to-transparent border-t border-white/20 dark:border-white/10 -mx-8 -mb-8 mt-8" data-id="element-323">
                    <button type="submit" form="checkout-form" disabled={isProcessingPayment} className="w-full flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white py-5 rounded-xl font-extrabold text-lg transition-all group disabled:opacity-70 shadow-[0_0_20px_rgba(255,140,50,0.3)] hover:shadow-[0_0_25px_rgba(255,140,50,0.5)]" data-id="element-324">
                      {isProcessingPayment ? <>
                          <Loader2 className="w-6 h-6 mr-2 animate-spin" data-id="element-325" />
                          Processing Order...
                        </> : <>
                          Submit Order Request
                          <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-326" />
                        </>}
                    </button>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW ===== */}
              {view === 'success' && <div className="flex-1 flex flex-col items-center justify-center text-center p-10 space-y-8 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/40 to-transparent dark:from-white/5 dark:to-transparent" data-id="element-327">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 15,
              stiffness: 200
            }} className="w-24 h-24 bg-[#2e8a44]/20 dark:bg-[#2e8a44]/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-[#2e8a44]/30 shadow-[0_0_30px_rgba(46,138,68,0.2)]" data-id="element-328">
                    <CheckCircle2 className="w-12 h-12 text-[#2e8a44] dark:text-[#2e8a44]" data-id="element-329" />
                  </motion.div>
                  <div data-id="element-330">
                    <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-331">
                      Order Request Submitted!
                    </h3>
                    <p className="text-brand-muted dark:text-stone-300 max-w-sm mx-auto text-lg font-medium" data-id="element-332">
                      You will receive a confirmation via text message or email
                      shortly.
                    </p>
                  </div>
                  <button onClick={handleCloseSuccess} className="bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white px-10 py-4 rounded-xl font-extrabold text-lg transition-colors border border-transparent dark:border-white/10 backdrop-blur-sm" data-id="element-333">
                    Done
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}